// item 153 — deterministic per-user identity color. Hash the userId into a curated,
// reasonably color-blind-distinct palette so a user wears the SAME color everywhere
// (chat, commissions, audit "by" fields) with ZERO storage / migration. Pure function.

// Curated palette — saturated, distinct hues that read on both light + dark surfaces.
// Each entry carries a solid bg + a contrasting text color for the chip's initials.
export interface UserColor {
  bg: string;
  fg: string;
}

const PALETTE: UserColor[] = [
  { bg: '#2563eb', fg: '#ffffff' }, // blue
  { bg: '#59d140', fg: '#0b1f0a' }, // brand green
  { bg: '#d946ef', fg: '#ffffff' }, // magenta
  { bg: '#f59e0b', fg: '#1a1206' }, // amber
  { bg: '#0ea5e9', fg: '#04212e' }, // sky
  { bg: '#ef4444', fg: '#ffffff' }, // red
  { bg: '#14b8a6', fg: '#04221e' }, // teal
  { bg: '#a855f7', fg: '#ffffff' }, // purple
  { bg: '#f97316', fg: '#1a0d03' }, // orange
  { bg: '#10b981', fg: '#04241a' }, // emerald
  { bg: '#ec4899', fg: '#ffffff' }, // pink
  { bg: '#6366f1', fg: '#ffffff' }, // indigo
];

// stable string hash (djb2) → always-positive index into the palette
const hash = (s: string): number => {
  let h = 5381;
  for (let i = 0; i < s.length; i += 1) {
    // eslint-disable-next-line no-bitwise
    h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
};

export const userColor = (userId?: string | null): UserColor => {
  if (!userId) return { bg: '#94a3b8', fg: '#ffffff' }; // neutral slate when unknown
  return PALETTE[hash(userId) % PALETTE.length];
};

// up to two initials from a display name (falls back to '?')
export const userInitials = (name?: string | null): string => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};
